import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import { Box } from '@mui/system';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { BrowserRouter, Routes, Route, Outlet, useLocation, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import AlertPopUp from './components/Alert/AlertPopUp';
import AboutUs from './components/LandingPage/AboutUs';
import Careers from './components/LandingPage/Careers';
import FAQ from './components/LandingPage/FAQ';
import ViewOrganizationPopup from './components/LandingPage/Org/ViewOrganizations';
import Plans from './components/LandingPage/PricingTab';
import PrivacyPolicy from './components/LandingPage/PrivacyPolicy';
import OurProducts from './components/LandingPage/Products';
import RefundCancellation from './components/LandingPage/RefundCancellation';
import TermsAndConditions from './components/LandingPage/TermsAndCondition';
import { LazyComponentWithSuspense } from './components/LazyLoader';
import StorageRestrictPopUp from './components/RestrictPopUp/index';
import SideBarResponsive, { DRAWER_WIDTH, MINI_DRAWER_WIDTH } from './components/SideBarResponsive';
import BigLoader from './components/Skeleton/BigLoader';
import { getOrgData, getOrgDomain } from './organization';
import { getTenantFromSubdomain } from './utils/appendOrgQuery';
import ContactSupport from './pages/ContactSupport/ContactSupport';
import CourseUnderReview from './pages/Courses/CourseUnderReview';
import EmailInvite from './pages/Invite/EmailInvite';
import Onboarding from './pages/Onboarding';
import OrganizationHomePage from './pages/Onboarding/Dashboard';
import { OrganizationRoutes } from './Routes/OrganizationRoutes';
import ProtectedRoute from './Routes/ProtectedRoute';
import PublicRoute from './Routes/PublicRoute';
import { imageHandler } from './utils/imageHandler';
const AdminConsole = LazyComponentWithSuspense(() => import('./pages/AdminConsole'));

const Home = LazyComponentWithSuspense(() => import('./pages/Courses/Home'));
const LandingPage = LazyComponentWithSuspense(() => import('./pages/LandingPage'));
const UserProfile = LazyComponentWithSuspense(() => import('./pages/Contest/UserProfile2'));
const MyCourses = LazyComponentWithSuspense(() => import('./pages/Courses/MyCourses'));
const SignIn = LazyComponentWithSuspense(() => import('./pages/SignInSignUp/SignIn'));
const SignUp = LazyComponentWithSuspense(() => import('./pages/SignInSignUp/SignUp'));
const PageNotFound = LazyComponentWithSuspense(() => import('./pages/PageNotFound/PageNotFound'));
const PublicResolver = LazyComponentWithSuspense(() => import('./pages/Public/Resolver'));
const VerifyUser = LazyComponentWithSuspense(() => import('./pages/SignInSignUp/Verifyuser'));
const UserWithoutLogin = LazyComponentWithSuspense(
  () => import('./pages/Courses/UserWithoutLogin')
);
const ForgotPassword = LazyComponentWithSuspense(
  () => import('./pages/ForgotPassword/ForgotPasswordEmail')
);
const ResetPassword = LazyComponentWithSuspense(
  () => import('./pages/ForgotPassword/ConfirmPassword')
);
const SessionTimedOut = LazyComponentWithSuspense(
  () => import('./pages/SessionTimedOut/SessionTimedOut')
);
const CheckOut = LazyComponentWithSuspense(() => import('./pages/Courses/CheckOut'));
const PublicMeet = LazyComponentWithSuspense(() => import('./pages/PublicMeet'));
const InternalServerError = LazyComponentWithSuspense(
  () => import('./pages/InternalServerError/InternalServerError')
);

function App() {
  const [isSubDomainOrganization, setisSubDomainOrganization] = useState(false);
  const [selectedOrgName, setSelectedOrgName] = useState(null);
  const [orgImageStatus, setorgImageStatus] = useState('');
  const [is404Page, setIs404Page] = useState(false);
  const [accessStatus, setAccessStatus] = useState('allowed');

  const getDomainData = async() => {
    // Check if subdomain exists (tenant flow)
    const subdomain = getOrgDomain();

    // If subdomain exists, it's a tenant subdomain - skip organization lookup
    // Tenant flow uses tenant header, not organization lookup by domain
    if (subdomain) {
      setisSubDomainOrganization(true);
      return;
    }

    // Only lookup organization if no subdomain (legacy organization-by-domain flow)
    const data = await getOrgData();
    if (data) {
      setisSubDomainOrganization(true);
    }
  };

  useEffect(() => {
    getDomainData();
    imageHandler.fetchAllImages();
  }, []);

  const Layout = () => {
    const location = useLocation();

    const isProblemRoute = /\/problem\/[a-zA-Z0-9-]+$/.test(location.pathname);

    const isCoursePage =
      location.pathname.includes('/course/videos') ||
      location.pathname.includes('/course/upload') ||
      location.pathname.includes('/course/create') ||
      location.pathname.includes('course/update');

    const showSidebar = !isProblemRoute && !is404Page && accessStatus === 'allowed';

    const effectiveSidebarWidth = isCoursePage ? MINI_DRAWER_WIDTH : DRAWER_WIDTH;

    const getIs404Page = () => {
      return (
        location.pathname.includes('/does-not-exist-definitely') || document.title.includes('404')
      );
    };

    useEffect(() => {
      setTimeout(() => {
        setIs404Page(getIs404Page());
      }, 10);
    }, [location]);

    return (
      <>
        {showSidebar && (
          <SideBarResponsive
            setSelectedOrgName={setSelectedOrgName}
            orgImageStatus={orgImageStatus}
            setorgImageStatus={setorgImageStatus}
          />
        )}
        {selectedOrgName && (
          <ViewOrganizationPopup
            selectedOrgName={selectedOrgName}
            setSelectedOrgName={setSelectedOrgName}
          />
        )}
        {<StorageRestrictPopUp />}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            width: showSidebar
              ? { xs: '100%', sm: `calc(100% - ${effectiveSidebarWidth}px)` }
              : '100%',
            ml: showSidebar ? { xs: 0, sm: `${effectiveSidebarWidth}px` } : 0,
            boxSizing: 'border-box'
          }}
        >
          <Outlet />
        </Box>
      </>
    );
  };

  // Component to handle ViewOrganizationPopup with Router context
  const OrganizationPopupHandler = () => {
    const location = useLocation();

    return selectedOrgName && ['/', '/organization'].includes(location.pathname) ? (
      <ViewOrganizationPopup
        selectedOrgName={selectedOrgName}
        setSelectedOrgName={setSelectedOrgName}
      />
    ) : null;
  };
  const clientId = process.env.REACT_APP_PUBLIC_GOOGLE_CLIENT_ID;

  return (
    <GoogleOAuthProvider clientId={clientId}>
      <div className="App theme-container">
        <BrowserRouter>
          <Box
            sx={{
              flexGrow: 1,
              width: { lg: 'calc(100%)' },
              minWidth: { lg: '978px' },
              margin: 'auto!important',
              maxWidth: { xl: '1640px' }
            }}
          >
            <OrganizationPopupHandler />
            {/* <Box>
                <Fab
                  variant="extended"
                  size={'medium'}
                  color="primary"
                  aria-label="add"
                  sx={{
                    p: 2,
                    position: 'fixed',
                    bottom: { xs: '48px', sm: '24px' },
                    right: { xs: '16px', sm: '24px' },
                    zIndex: 1200,
                  }}
                  onClick={() => (window.location.href = '/contact-support')}
                >
                  Report a Bug
                </Fab>
              </Box> */}
            {/* <Dialogue
                opendia={openDialog}
                setOpendia={setOpenDialog}
                title={'Report a Bug'}
                children={<ReportABug setOpendia={setOpenDialog} />}
                maxWidth="500px"
              /> */}
            <AlertPopUp />
            <>
              <ToastContainer />
              <React.Suspense fallback={<BigLoader />}>
                <Routes>
                  <Route path="/" element={<LandingPage />} />
                  <Route element={<ProtectedRoute />}>
                    <Route
                      path="/organization"
                      element={<OrganizationHomePage setSelectedOrgName={setSelectedOrgName} />}
                    />
                    <Route path="/onboarding" element={<Onboarding />} />
                    {/* Platform admin console. Deliberately OUTSIDE <Layout>:
                        it spans every organisation, so the org sidebar has no
                        org to render from and throws on item.name. */}
                    <Route path="/console" element={<AdminConsole />} />
                    <Route path="/" element={<Layout />}>
                      <Route path="/dashboard" element={<Home />} />
                      <Route path="/user-profile" element={<UserProfile />} />
                      <Route path="/user-profile/:slug" element={<UserProfile />} />
                      <Route path="/my-courses" element={<MyCourses />} />
                      <Route
                        path="/org/:orgName/*"
                        element={
                          <OrganizationRoutes
                            orgImageStatus={orgImageStatus}
                            setorgImageStatus={setorgImageStatus}
                            setAccessStatus={setAccessStatus}
                          />
                        }
                      />
                      {/* <Route path="admin/*" element={<AdminRoutes />} /> */}
                      {/* <Route path="problem/*" element={<ProblemRoutes />} /> */}
                    </Route>
                  </Route>
                  <Route element={<PublicRoute />}>
                    <Route path="/sign-in" element={<SignIn />} />
                    {isSubDomainOrganization ? (
                      <Route path="/sign-up" element={<Navigate to="/sign-in" replace />} />
                    ) : (
                      <Route path="/sign-up" element={<SignUp />} />
                    )}
                  </Route>
                  <Route path="/meet/:code" element={<PublicMeet />} />
                  <Route path="/p/user/:slug" element={<PublicResolver />} />
                  <Route path="/p/org/:slug" element={<PublicResolver />} />
                  <Route path="/p/course/:slug" element={<PublicResolver />} />
                  <Route path="/p/task/:slug" element={<PublicResolver />} />
                  <Route path="/p/contest/:slug" element={<PublicResolver />} />
                  <Route path="/user-without-login" element={<UserWithoutLogin />} />{' '}
                  {/* needs attention */}
                  <Route path="/contact-support" element={<ContactSupport />} />
                  <Route path="/about-us" element={<AboutUs />} />
                  <Route path="/our-products" element={<OurProducts />} />
                  <Route path="/plans" element={<Plans />} />
                  <Route path="/careers" element={<Careers />} />
                  <Route path="/resetpassword/:id" element={<ResetPassword />} />
                  <Route path="/verifyuser/:id" element={<VerifyUser />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/session-expired" element={<SessionTimedOut />} />
                  <Route path="/internal-server-error" element={<InternalServerError />} />
                  <Route path="/terms-conditions" element={<TermsAndConditions />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="/faq" element={<FAQ />} />
                  <Route path="/refund-cancellation" element={<RefundCancellation />} />
                  <Route
                    path="/email/invite/:inviteId"
                    element={<EmailInvite setSelectedOrgName={setSelectedOrgName} />}
                  />
                  <Route path="/contact-support" element={<ContactSupport />} />
                  <Route path="/under-review" element={<CourseUnderReview />} />
                  <Route path="/checkout" element={<CheckOut />} />
                  <Route path="*" element={<PageNotFound />} />
                </Routes>
              </React.Suspense>
            </>
          </Box>
        </BrowserRouter>
      </div>
    </GoogleOAuthProvider>
  );
}

export default App;
